'use client';
import { useRef } from 'react';
import Link from 'next/link';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

import { SM_BREAKPOINT } from '@/lib/constants';

type TestimonialCardProps = {
  videoSrc: string;
  testimonial: React.ReactNode;
  author: string;
  url: string;
  videoSrcType: string;
};

const TestimonialCard = ({ videoSrc, videoSrcType, testimonial, author, url }: TestimonialCardProps) => {
  const container = useRef(null);

  const videoRef = useRef<HTMLVideoElement>(null);

  gsap.registerPlugin(ScrollTrigger);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(
        {
          isMobile: `(max-width: ${SM_BREAKPOINT}px)`,
          isDesktop: `(min-width: ${SM_BREAKPOINT + 1}px)`,
        },
        ctx => {
          const { isMobile } = ctx.conditions as gsap.Conditions;

          gsap.timeline({
            scrollTrigger: {
              trigger: container.current,
              start: isMobile ? '25% center' : 'top center',
              end: 'bottom center',
              onEnter: () => {
                videoRef.current?.play();
              },
              onEnterBack: () => {
                videoRef.current?.play();
              },
              onLeave: () => {
                videoRef.current?.pause();
              },
              onLeaveBack: () => {
                videoRef.current?.pause();
              },
            },
          });
        },
      );
    },
    { scope: container },
  );
  return (
    <div ref={container} id="testimonial" className="px-0 md:px-10">
      <div className="max-w-section flex flex-col gap-10 rounded-3xl bg-[#18181a] p-8 md:p-10 lg:flex-row lg:gap-20">
        <div className="flex flex-1 basis-[55%] flex-col justify-center gap-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="48" viewBox="0 0 64 48" fill="none">
            <path
              d="M37.3787 0.5V19.7987C37.3787 34.6925 47.328 44.7871 61.3333 47.5L63.9867 41.8835C57.5013 39.4891 53.3333 32.3843 53.3333 26.6111H64V0.5H37.3787ZM0 0.5V19.7987C0 34.6925 9.99467 44.7897 24 47.5L26.656 41.8835C20.168 39.4891 16 32.3843 16 26.6111H26.6213V0.5H0Z"
              fill="white"
            />
          </svg>
          <p className="text-h6">{testimonial}</p>
          <div className="flex flex-col gap-2">
            <p>{author}</p>
          </div>
        </div>
        <div className="flex-1 basis-[45%]">
          <Link href={url} target="blank" className="flex justify-center">
            <video
              ref={videoRef}
              muted
              playsInline
              className="h-[250px] w-full max-w-[500px] rounded-3xl object-cover md:h-[400px] lg:max-w-full xl:h-[450px]"
            >
              <source src={videoSrc} type={videoSrcType} />
            </video>
          </Link>
        </div>
      </div>
    </div>
  );
};
export default TestimonialCard;
